import React, { useEffect, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { Divider, Grid } from "@material-ui/core";
import BankingInformationField from "./BankingInformationField";
import PropTypes from "prop-types";
import { bankInformationStatuses } from "constants/amoTableDropdownOptions";
import { fileAssociations, fileTypes } from "constants/fileTypes";
import { bankingInformationCodes } from "constants/formContentManagement";
import NumberFormat from "react-number-format";
import AmoTooltip from "./AmoTooltip";
import FileUploadList from "./FileUploadList";

const useStyles = makeStyles((theme) => ({
  divider: {
    background: "#F1F1F1",
  },
  leftOverviewItem: {
    marginLeft: "0rem",
  },
}));

export const NumberFormatCustom = React.forwardRef((props, ref) => {
  // TODO: Should we pass the inputRef from MUI through to the input component? If not, comment why.
  const { onChange, isNegative, format, inputRef, ...other } = props;
  return (
    <NumberFormat
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...other}
      getInputRef={ref}
      onValueChange={(values) => {
        onChange({
          target: {
            name: props.name,
            value: values.formattedValue,
          },
        });
      }}
      thousandSeparator
      allowNegative={false}
      format={format}
    />
  );
});

NumberFormatCustom.propTypes = {
  name: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  isNegative: PropTypes.bool.isRequired,
  format: PropTypes.string.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  inputRef: PropTypes.any,
};

NumberFormatCustom.defaultProps = {
  inputRef: null,
};

/**
 * A form component to use on the banking information page and modal
 *
 * @param {object} props - object containing props for this component
 * @param {object} props.control - react hook form control
 * @param {object} props.getValues - react hook form getValues
 * @param {object} props.bankingInformation - banking information data
 * @param {boolean} props.isEditMode - flag to indicate wheter fields should be read only or editable
 * @param {boolean} props.isModal - flag to adjust style depending on whether this is a modal or not
 * @param {boolean} props.shouldShowPreviousValues - show or hide previous values for fields depends on a user role
 * @param {object} props.formFields - object containing the information of the fields in this form
 * @param {Function} props.onFileChange - Function checks if a file is provided with FileUploadList component
 * @param {Function} props.hasBankingFieldsChanged - Function shows if Banking fields were changed
 * @returns {React.Component} The banking information form component
 */
const BankingInformationForm = (props) => {
  const {
    control,
    getValues,
    bankingInformation,
    isEditMode,
    isModal,
    shouldShowPreviousValues,
    formFields,
    onFileChange,
    hasBankingFieldsChanged,
  } = props;
  const classes = useStyles();

  const [initialFile, setInitialFile] = useState([]);
  const [fileUploadAction, setFileUploadAction] = useState("Idle");

  const onFileRollback = () => {
    setFileUploadAction("Rollback");
  };

  const statusesToShowFileUpload = [
    bankInformationStatuses.Changed,
    bankInformationStatuses["Pending Review"],
  ];

  // left side of term for showFileUploadField and isActiveFileUploadField is responsible for a Municipal user logic and right one is for an AMO user
  const showFileUploadField = () =>
    isEditMode ||
    (isModal &&
      statusesToShowFileUpload.includes(bankingInformation.status ?? ""));
  const isActiveFileUploadField = () =>
    hasBankingFieldsChanged() ||
    (isModal &&
      statusesToShowFileUpload.includes(bankingInformation.status ?? ""));

  useEffect(() => {
    if (bankingInformation?.fileId && bankingInformation?.fileName) {
      setInitialFile([
        { id: bankingInformation?.fileId, name: bankingInformation?.fileName },
      ]);
    }
  }, [bankingInformation]);

  const uploadFileComponent = (
    <FileUploadList
      key={initialFile.length ? initialFile[0].id : "default"}
      fileAssociationId={fileAssociations.Banking}
      fileAssociationObjectId={bankingInformation.id}
      municipalityId={bankingInformation.municipalityId}
      multiple={false}
      showAllowedTypesHint
      allowedTypes={[
        fileTypes.pdf,
        fileTypes.jpg,
        fileTypes.jpeg,
        fileTypes.png,
      ]}
      initialValue={initialFile}
      readonly={!isActiveFileUploadField()}
      disabled={!isActiveFileUploadField()}
      onChange={(files) => {
        if ((initialFile[0]?.id ?? 0) !== files[0]?.id) {
          onFileChange(files);
        }
      }}
      uploadAction={fileUploadAction}
      onRollback={onFileRollback}
    />
  );

  return (
    <Grid item container spacing={4}>
      <Grid item container xs={12}>
        <Grid item xs={isModal ? 6 : 5}>
          <BankingInformationField
            name="municipalityName"
            label="Municipality"
            value={bankingInformation.municipalityName}
            control={control}
            overrideClassName={isModal ? classes.leftOverviewItem : ""}
          />
        </Grid>

        <Grid item xs={6}>
          <BankingInformationField
            name="treasurerName"
            label="Treasurer"
            value={bankingInformation.treasurerName}
            control={control}
          />
        </Grid>
      </Grid>

      <Divider className={classes.divider} />

      <Grid item container xs={12}>
        <Grid item xs={isModal ? 6 : 5}>
          <BankingInformationField
            name="contactName"
            label={formFields[bankingInformationCodes.fieldContactName].text}
            value={bankingInformation.contactName}
            editMode={isEditMode}
            control={control}
            overrideClassName={isModal ? classes.leftOverviewItem : ""}
            required
            helperText={
              formFields[bankingInformationCodes.fieldContactName].helperText
            }
            tooltipText={
              formFields[bankingInformationCodes.fieldContactName].tooltipText
            }
          />
        </Grid>

        <Grid item xs={6}>
          <BankingInformationField
            name="contactEmail"
            label={formFields[bankingInformationCodes.fieldContactEmail].text}
            value={bankingInformation.contactEmail}
            editMode={isEditMode}
            control={control}
            required
            helperText={
              formFields[bankingInformationCodes.fieldContactEmail].helperText
            }
            tooltipText={
              formFields[bankingInformationCodes.fieldContactEmail].tooltipText
            }
          />
        </Grid>
      </Grid>

      <Grid item container xs={12}>
        <Grid item xs={isModal ? 6 : 5}>
          <BankingInformationField
            name="contactPhone"
            label={formFields[bankingInformationCodes.fieldContactPhone].text}
            value={bankingInformation.contactPhone}
            editMode={isEditMode}
            control={control}
            overrideClassName={isModal ? classes.leftOverviewItem : ""}
            required
            helperText={
              formFields[bankingInformationCodes.fieldContactPhone].helperText
            }
            tooltipText={
              formFields[bankingInformationCodes.fieldContactPhone].tooltipText
            }
            phoneFormat
            inputProps={{
              inputComponent: NumberFormatCustom,
              inputProps: {
                format: "###-###-####",
                isNegative: false,
              },
              onBlur: () => {},
            }}
            inputLabelProps={{
              shrink: !["", undefined, null].includes(
                getValues("contactPhone")
              ),
            }}
          />
        </Grid>

        <Grid item xs={6}>
          <BankingInformationField
            name="phoneExtension"
            label={
              formFields[bankingInformationCodes.fieldContactPhoneExtension]
                ?.text
            }
            value={bankingInformation.phoneExtension}
            editMode={isEditMode}
            control={control}
            helperText={
              formFields[bankingInformationCodes.fieldContactPhoneExtension]
                .helperText
            }
            tooltipText={
              formFields[bankingInformationCodes.fieldContactPhoneExtension]
                .tooltipText
            }
            inputProps={{
              inputComponent: NumberFormatCustom,
              inputProps: {
                format: "##########",
                isNegative: false,
              },
              onBlur: () => {},
            }}
            inputLabelProps={{
              shrink: !["", undefined, null].includes(
                getValues("phoneExtension")
              ),
            }}
          />
        </Grid>
      </Grid>

      <Divider className={classes.divider} />

      <Grid item container xs={12}>
        <Grid item xs={isModal ? 6 : 5}>
          <BankingInformationField
            name="institutionName"
            label={
              formFields[bankingInformationCodes.fieldFinancialInstitution].text
            }
            value={bankingInformation.institutionName}
            previousValue={
              shouldShowPreviousValues
                ? bankingInformation.lastApprovedInstitutionName
                : null
            }
            editMode={isEditMode}
            control={control}
            overrideClassName={isModal ? classes.leftOverviewItem : ""}
            required
            helperText={
              formFields[bankingInformationCodes.fieldFinancialInstitution]
                .helperText
            }
            tooltipText={
              formFields[bankingInformationCodes.fieldFinancialInstitution]
                .tooltipText
            }
          />
        </Grid>

        <Grid item xs={6}>
          <BankingInformationField
            name="institutionNumber"
            label={
              formFields[bankingInformationCodes.fieldInstitutionNumber].text
            }
            value={bankingInformation.institutionNumber}
            previousValue={
              shouldShowPreviousValues
                ? bankingInformation.lastApprovedInstitutionNumber
                : null
            }
            editMode={isEditMode}
            control={control}
            required
            helperText={
              formFields[bankingInformationCodes.fieldInstitutionNumber]
                .helperText
            }
            tooltipText={
              formFields[bankingInformationCodes.fieldInstitutionNumber]
                .tooltipText
            }
          />
        </Grid>
      </Grid>

      <Grid item container xs={12}>
        <Grid item xs={isModal ? 6 : 5}>
          <BankingInformationField
            name="transitNumber"
            label={formFields[bankingInformationCodes.fieldTransitNumber].text}
            value={bankingInformation.transitNumber}
            previousValue={
              shouldShowPreviousValues
                ? bankingInformation.lastApprovedTransitNumber
                : null
            }
            editMode={isEditMode}
            control={control}
            overrideClassName={isModal ? classes.leftOverviewItem : ""}
            required
            helperText={
              formFields[bankingInformationCodes.fieldTransitNumber].helperText
            }
            tooltipText={
              formFields[bankingInformationCodes.fieldTransitNumber].tooltipText
            }
          />
        </Grid>

        <Grid item xs={6}>
          <BankingInformationField
            name="accountNumber"
            label={formFields[bankingInformationCodes.fieldAccountNumber].text}
            value={bankingInformation.accountNumber}
            previousValue={
              shouldShowPreviousValues
                ? bankingInformation.lastApprovedAccountNumber
                : null
            }
            editMode={isEditMode}
            control={control}
            required
            helperText={
              formFields[bankingInformationCodes.fieldAccountNumber].helperText
            }
            tooltipText={
              formFields[bankingInformationCodes.fieldAccountNumber].tooltipText
            }
          />
        </Grid>
      </Grid>
      {showFileUploadField() ? (
        <Grid item container xs={isModal ? 12 : 6}>
          <Grid item xs={isModal ? 5 : 5}>
            {isActiveFileUploadField() ? (
              uploadFileComponent
            ) : (
              <AmoTooltip
                tooltipText="An upload is only required when banking information is changed."
                customComponent={uploadFileComponent}
              />
            )}
          </Grid>
        </Grid>
      ) : null}
    </Grid>
  );
};

BankingInformationForm.propTypes = {
  control: PropTypes.shape(),
  getValues: PropTypes.func,
  bankingInformation: PropTypes.shape().isRequired,
  isEditMode: PropTypes.bool,
  isModal: PropTypes.bool,
  shouldShowPreviousValues: PropTypes.bool,
  formFields: PropTypes.shape().isRequired,
  onFileChange: PropTypes.func,
  hasBankingFieldsChanged: PropTypes.func,
};

BankingInformationForm.defaultProps = {
  isEditMode: false,
  shouldShowPreviousValues: true,
  control: {},
  getValues: () => {},
  isModal: false,
  onFileChange: () => {},
  hasBankingFieldsChanged: () => {},
};

export default BankingInformationForm;
